import { makeAccessible } from '@/utils/makeAccessible';
import { ColumnMeta, type Header, flexRender } from '@tanstack/react-table';
import { twMerge } from 'tailwind-merge';
import { alignStyles } from '../../TableBase/constants';
import { TableElement } from '../../types';

type TableHeaderCellProps<T extends TableElement> = {
  header: Header<T, unknown>;
  onHeaderClick: (e: React.MouseEvent, header: Header<T, unknown>) => void;
  classNames?: {
    thClassName?: string;
    contentWrapperClassName?: string;
  };
};

export const TableHeaderCell = <T extends TableElement>({
  header,
  onHeaderClick,
  classNames: { thClassName, contentWrapperClassName } = {},
}: TableHeaderCellProps<T>) => {
  const meta = header.column.columnDef.meta as ColumnMeta<T, unknown>;
  const headerContent = flexRender(header.column.columnDef.header, header.getContext());
  const { cellClassName, headerClassName, headerTextClassName } = alignStyles(meta?.align);

  const sortingDirection = typeof header.column.getIsSorted() === 'boolean' ? 'empty' : header.column.getIsSorted();
  const isAsc = sortingDirection === 'asc';
  const isSortable = header.column.getCanSort();

  return (
    <th
      align={meta?.align}
      key={header.id}
      {...makeAccessible((e) => onHeaderClick(e, header))}
      tabIndex={-1}
      style={{
        maxWidth: header.column.columnDef.maxSize,
        minWidth: header.column.columnDef.minSize,
        width: header.column.columnDef.size,
      }}
      className={twMerge(
        'group/headerCell border-y border-secondary-border first:border-s last:border-e px-6 py-3 text-xs font-semibold first-letter:capitalize leading-5 text-quaternary-500 group-first/header:first:rounded-tl-lg group-first/header:last:rounded-tr-lg',
        thClassName
      )}
    >
      <div className={twMerge('flex h-full items-center gap-x-1', headerClassName, contentWrapperClassName)}>
        <div
          className={twMerge(
            'flex h-full flex-1 select-none items-center gap-1',
            headerTextClassName,
            isSortable ? 'group-hover/headerCell:text-quaternary-600 cursor-pointer' : 'cursor-auto',
            'motion-safe:transition-colors motion-safe:duration-150 motion-reduce:transition-none',
            cellClassName
          )}
        >
          <div>{headerContent}</div>
          {isSortable &&
            (sortingDirection === 'empty' ? (
              <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  className='stroke-quaternary-400 group-hover/headerCell:stroke-quaternary-600 motion-safe:transition-colors motion-safe:duration-150 motion-reduce:transition-none'
                  d='M3.5 7.5L6 10L8.5 7.5M3.5 4.5L6 2L8.5 4.5'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                className={twMerge(isAsc ? 'rotate-180' : undefined)}
                width='12'
                height='12'
                viewBox='0 0 12 12'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  className='stroke-quaternary-400 group-hover/headerCell:stroke-quaternary-600 motion-safe:transition-colors motion-safe:duration-150 motion-reduce:transition-none'
                  d='M6 2.5V9.5M6 9.5L9.5 6M6 9.5L2.5 6'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ))}
        </div>
      </div>
    </th>
  );
};
